import type { BaseQueryFn } from "@reduxjs/toolkit/query/react"

type FetchError = { status: "FETCH_ERROR"; error: string }

/**
 * The most telemetry requests that may be in flight at once, and what a request
 * with no range asked for gets. Sized against what the stack runs in parallel,
 * not what the browser will open: the API serves ten requests per pod and
 * InfluxDB runs twenty queries at a time, and the time a query spends waiting
 * there counts against its ten second timeout.
 */
export const MAX_CONCURRENT_TELEMETRY_REQUESTS = 6

const HOUR = 60 * 60 * 1000
const DAY = 24 * HOUR

/**
 * The cap comes down as the range goes up: a year of telemetry costs the store
 * far more than an hour of it, so a flat number either starves the short ranges
 * or drowns the long ones. Read in order, first match wins.
 *
 * The narrowing starts past a week, not past a day, because that is where the
 * failures are: both defaults a dashboard opens on — `lastWeek` for device and
 * tower, `lastDayPlusSixHours` for the entities carrying a forecast — sit at or
 * under seven days, and neither has been giving the store trouble. Cutting at a
 * day would have taken the full pool away from every tab to buy nothing.
 */
const CAP_BY_RANGE: readonly { upTo: number; cap: number }[] = [
  { upTo: 7 * DAY, cap: MAX_CONCURRENT_TELEMETRY_REQUESTS },
  { upTo: 14 * DAY, cap: 4 },
  // 31 days rather than 30, so the "last month" preset always lands here
  // whatever the calendar month it subtracted.
  { upTo: 31 * DAY, cap: 2 },
]

/** Anything past the table — a quarter, a year — runs one at a time. */
const CAP_BEYOND_TABLE = 1

/** Everything in SERIES starts before anything in QUALITY. */
const SERIES = 0
const QUALITY = 1

// Listed by name rather than matched by pattern, so a new endpoint has to opt
// in instead of being throttled by accident.
const SERIES_ENDPOINTS = new Set([
  "deviceTelemetry",
  "spanTelemetry",
  "towerTelemetry",
  "powerlineTelemetry",
  "weatherService",
])

const QUALITY_ENDPOINTS = new Set([
  "deviceTelemetryQuality",
  "spanTelemetryQuality",
  "towerTelemetryQuality",
  "powerlineTelemetryQuality",
])

/**
 * The lane a request queues in, or `null` when it skips the queue. Only
 * telemetry is scheduled: the rest of the API answers what the charts need to
 * build their own arguments, so holding it back would push the charts further
 * away rather than closer. Quality goes last because the chart draws without it.
 */
export const requestLane = (
  endpoint: string,
  type: "query" | "mutation",
): number | null => {
  if (type !== "query") return null
  if (SERIES_ENDPOINTS.has(endpoint)) return SERIES
  if (QUALITY_ENDPOINTS.has(endpoint)) return QUALITY
  return null
}

const rangeParams = (args: unknown): { from: string; to: string } | null => {
  if (typeof args !== "object" || args === null) return null
  const { params } = args as { params?: { from_date?: unknown; to_date?: unknown } }
  if (typeof params?.from_date !== "string" || typeof params?.to_date !== "string") {
    return null
  }
  return { from: params.from_date, to: params.to_date }
}

/** How long a stretch of time the request asks for, in ms. */
const requestedRange = (args: unknown): number | null => {
  const params = rangeParams(args)
  if (params === null) return null
  const length = Date.parse(params.to) - Date.parse(params.from)
  return Number.isFinite(length) && length > 0 ? length : null
}

/**
 * What ties a request to the wave it belongs to, or `null` when it belongs to
 * none. Every request a dashboard fires for one range shares its `to_date` —
 * the forecast variants differ only in `from_date`, so keying on both ends
 * would split one wave in two and supersede half of it.
 *
 * It does not follow that a moved `to_date` is what makes a wave stale: a
 * custom range edited only at the start keeps its `to_date` and leaves its
 * queued queries behind all the same. That call is `dateRangeMiddleware`'s,
 * which watches both ends and cancels by this key.
 */
export const requestWave = (args: unknown): string | null => rangeParams(args)?.to ?? null

/**
 * How many requests of this size may run at once. Taken from the dates on the
 * request rather than from the picker, so a custom range and a zoom are covered
 * by the same rule and no preset name has to reach the queue.
 */
export const requestCap = (args: unknown): number => {
  const range = requestedRange(args)
  if (range === null) return MAX_CONCURRENT_TELEMETRY_REQUESTS
  return CAP_BY_RANGE.find(({ upTo }) => range <= upTo)?.cap ?? CAP_BEYOND_TABLE
}

/** How a waiter left the queue. Only `start` ever reaches the network. */
type Outcome = "start" | "aborted" | "superseded"

interface Waiter {
  lane: number
  cap: number
  wave: string | null
  signal: AbortSignal
  /** Releases the waiter, with a slot only when the outcome is `start`. */
  release: (outcome: Outcome) => void
}

/**
 * The cap of every request in flight. Kept as the list rather than a count, so
 * the pool can be measured against the strictest range currently running.
 */
const activeCaps: number[] = []
let activeSeries = 0
// Bumped by `resetRequestQueue`, so a release from before the reset knows not
// to decrement a count that was already zeroed.
let epoch = 0
const waiting: Waiter[][] = [[], []]

const nextLane = (): number | undefined => {
  if (waiting[SERIES].length > 0) return SERIES
  // The chart draws without its quality summary, so the summary gives way
  // until there is no series left to run - queued ones included, since the
  // plots of a dashboard unblock in waves as their entities resolve and a free
  // slot would otherwise go to whichever request happened to arrive first.
  if (activeSeries > 0) return undefined
  return waiting[QUALITY].length > 0 ? QUALITY : undefined
}

const startWaiting = () => {
  for (;;) {
    const lane = nextLane()
    if (lane === undefined) return
    const next = waiting[lane][0]
    // Checked here and not on the way in: a request whose plot is already gone
    // would spend a slot on an answer nobody reads.
    if (next.signal.aborted) {
      waiting[lane].shift()
      next.release("aborted")
      continue
    }
    // The strictest cap in play wins, the head's own included: a wave of
    // year-long queries drains to one at a time, and an hour-long one arriving
    // beside it does not reopen the room the long one just narrowed.
    if (activeCaps.length >= Math.min(next.cap, ...activeCaps)) return
    waiting[lane].shift()
    activeCaps.push(next.cap)
    if (lane === SERIES) activeSeries += 1
    next.release("start")
  }
}

/**
 * When each wave was superseded. A request sleeping between retry attempts is
 * in neither the queue nor the pool — the failed attempt released its slot and
 * `retry` is waiting out its backoff outside of here — so walking `waiting`
 * cannot reach it. The wave is marked instead, and the retry is refused when it
 * comes back for a slot.
 */
const supersededWaves = new Map<string, number>()

/**
 * How long a mark is worth *keeping*, measured against the longest a request
 * can sleep: three retries of jittered backoff, six seconds. It bounds the map,
 * nothing else — a mark stops refusing on its own, because what it is compared
 * against is when the request first reached the queue, and no request that
 * predates a mark this old is still alive. Pruned on write rather than on a
 * timer, so nothing is left pending behind a test.
 */
const SUPERSEDED_TTL = 10_000

/**
 * When each request first reached the queue. `retry` loops over the same `args`
 * object — `baseQuery(args, api, extraOptions)` inside its `while` — and every
 * endpoint builds a fresh one per fetch, so this is both what tells a retry from
 * a first attempt and what dates it.
 *
 * Dating it is what separates the wave a user left from the wave replacing it
 * when a custom range edited only at the start hands both of them the same
 * `to_date`: the one that was already queued before the mark is stale, the one
 * that arrived after it is the wave being waited on.
 */
const firstSeen = new WeakMap<object, number>()

/**
 * Drops everything still queued for `wave` and marks it, so a range the user
 * has moved on from never reaches the API at all — neither from the queue nor
 * from a retry that was asleep when the range moved. Requests already in flight
 * are left alone: the store is working on them either way, and cutting them
 * short would buy a free slot rather than a spared query.
 */
export const supersedeWave = (wave: string) => {
  const now = Date.now()
  supersededWaves.forEach((at, key) => {
    if (now - at > SUPERSEDED_TTL) supersededWaves.delete(key)
  })
  supersededWaves.set(wave, now)
  waiting.forEach((lane) => {
    for (let i = lane.length - 1; i >= 0; i -= 1) {
      if (lane[i].wave === wave) lane.splice(i, 1)[0].release("superseded")
    }
  })
  startWaiting()
}

const acquire = (
  lane: number,
  cap: number,
  wave: string | null,
  signal: AbortSignal,
): Promise<Outcome> =>
  new Promise((resolve) => {
    waiting[lane].push({ lane, cap, wave, signal, release: resolve })
    startWaiting()
  })

/**
 * A dropped waiter has to settle as something, and a base query has only data
 * or an error to settle as. An error is safe here where an abort would not be:
 * RTK deletes a query from its running map on `statePromise.then`, which an
 * abort rejects past and an error result reaches, and it leaves no
 * `fulfilledTimeStamp`, so a later dispatch of the same key fetches again
 * instead of resolving from the dead entry.
 */
const SUPERSEDED_ERROR = "Superseded by a newer range"

/** Whether a thrown error is the queue saying the range had moved on. */
export const isSuperseded = (error: unknown): boolean =>
  (error as { error?: unknown } | null | undefined)?.error === SUPERSEDED_ERROR

const queueRefusal: Record<Exclude<Outcome, "start">, { error: FetchError }> = {
  aborted: { error: { status: "FETCH_ERROR", error: "Aborted while queued" } },
  superseded: { error: { status: "FETCH_ERROR", error: SUPERSEDED_ERROR } },
}

/**
 * Caps how many telemetry requests run at once, so one slow query can't push
 * the rest of the dashboard past the API's Influx timeout. Goes inside `retry`,
 * so a retried attempt queues again and the backoff sleeps without holding a
 * slot.
 */
export const withRequestQueue =
  (baseQuery: BaseQueryFn): BaseQueryFn =>
  async (args, api, extraOptions) => {
    const lane = requestLane(api.endpoint, api.type)
    if (lane === null) return baseQuery(args, api, extraOptions)

    const cap = requestCap(args)
    const wave = requestWave(args)
    const identifiable = typeof args === "object" && args !== null
    const queuedAt = identifiable ? firstSeen.get(args) : undefined
    if (identifiable && queuedAt === undefined) firstSeen.set(args, Date.now())
    const markedAt = wave === null ? undefined : supersededWaves.get(wave)
    // What `supersedeWave` could not reach: a request that was asleep on a
    // retry backoff when the range moved and is only now asking for a slot
    // again. Refused as superseded, which is also what stops it retrying.
    //
    // Only a request the mark is younger than: a first attempt has no date yet,
    // and a retry of the wave that came *after* the mark is the one being
    // waited on — refusing it would leave its plot spinning, since the hook
    // treats a superseded result as somebody else's to finish. Ties go to the
    // newer wave, which is why this is strict: a retry of the older one always
    // has a round trip between its first sighting and the mark.
    if (queuedAt !== undefined && markedAt !== undefined && queuedAt < markedAt) {
      return queueRefusal.superseded
    }
    const outcome = await acquire(lane, cap, wave, api.signal)
    if (outcome !== "start") return queueRefusal[outcome]

    const startedAt = epoch
    try {
      return await baseQuery(args, api, extraOptions)
    } finally {
      if (startedAt === epoch) {
        const held = activeCaps.indexOf(cap)
        if (held !== -1) activeCaps.splice(held, 1)
        if (lane === SERIES) activeSeries -= 1
      }
      startWaiting()
    }
  }

/**
 * Test-only. A test that leaves a request in flight keeps holding its slot into
 * the next one, and the failure gives no hint why, so the global setup clears
 * the pool between tests.
 */
export const resetRequestQueue = () => {
  epoch += 1
  activeCaps.length = 0
  activeSeries = 0
  supersededWaves.clear()
  waiting.forEach((lane) => {
    lane.splice(0, lane.length).forEach((waiter) => waiter.release("aborted"))
  })
}
